import { execAdmin } from '@src/apis/reware/utils';
import type {
  GetPricingHistoryQuery,
  GetPricingHistoryQueryVariables,
} from '@src/types/__generated/rewareAdminTypes';

import type { PricingHistoryEntry } from './types';

const gql = String.raw;

/**
 * Fetches the list of past pricing-rule snapshots for a partner + pricing type.
 *
 * Each entry is one prior save (initiated/processed timestamps and the user who
 * triggered it). The list powers the "History" view on the Condition Discounts
 * page, where each row offers a TSV download of that snapshot's rules.
 */
export const fetchPricingHistory = async ({
  partnerId,
  pricingType,
}: GetPricingHistoryQueryVariables) => {
  const query = gql`
    query getPricingHistory($partnerId: Int!, $pricingType: String!) {
      partner(id: $partnerId) {
        pricingHistory(pricingType: $pricingType) {
          id
          partnerId
          userId
          createdAt
          initiatedAt
          processedAt
          user {
            id
            name
          }
        }
      }
    }
  `;

  const data = await execAdmin<GetPricingHistoryQueryVariables, GetPricingHistoryQuery>(
    query,
    { variables: { partnerId, pricingType } },
  );

  const history = data.partner?.pricingHistory;
  if (!history) {
    throw new Error(
      `fetchPricingHistory: unexpected response — partner.pricingHistory is ${
        history === null ? 'null' : 'undefined'
      } (partnerId=${partnerId}, pricingType=${pricingType})`,
    );
  }

  // The generated schema models elements as `Maybe<PricingDiscountHistoryAdmin>`.
  // Strip nulls so the caller (Datagrid renderer) can rely on `entry.id`/`createdAt`.
  return history.filter((entry): entry is PricingHistoryEntry => entry != null);
};
